import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Building2, Mail, Lock, ShieldCheck, ArrowLeft, Phone, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { COUNTRY_CODES, DEFAULT_DIAL } from "@/lib/countryCodes";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    society_nom: "",
    contact_nom: "",
    email: "",
    password: "",
    confirm: "",
    dial: DEFAULT_DIAL,
    phone: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (!acceptTerms) {
      setError("Vous devez accepter les conditions d'utilisation pour continuer.");
      return;
    }
    const phoneClean = form.phone.replace(/\s+/g, "").replace(/^0+/, "");
    const telephone = phoneClean ? `${form.dial} ${phoneClean}` : "";
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/auth/signup`, {
        society_nom: form.society_nom.trim(),
        contact_nom: form.contact_nom.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        telephone,
      });
      setSubmitted(true);
      toast.success("Demande envoyée");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      setError(detail || "Erreur lors de l'envoi de la demande.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left brand — desktop only */}
      <div className="hidden md:flex md:w-1/2 flex-col justify-between p-12 text-white relative overflow-hidden" style={{ backgroundColor: "#1F5F5B" }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #FFF 0%, transparent 40%), radial-gradient(circle at 70% 80%, #F97316 0%, transparent 45%)" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/25 flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="rimmoGET" className="w-14 h-14 object-cover" />
            </div>
            <div>
              <div className="font-heading font-bold text-2xl leading-none">rimmoGET</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300 mt-1.5">Gestion locative</div>
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading font-bold text-4xl leading-tight mb-4">
            Créez votre société<br />en quelques secondes.
          </h1>
          <p className="text-teal-100 text-base leading-relaxed max-w-md">
            Renseignez les informations ci-contre. La direction validera votre inscription, puis votre compte administrateur sera activé automatiquement.
          </p>
        </div>
        <div className="relative flex items-center gap-3 text-sm text-teal-100/80">
          <ShieldCheck className="w-4 h-4" /> Vos données sont chiffrées.
        </div>
      </div>

      {/* Right form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="signup-form">
          {/* Mobile-only brand header */}
          <div className="md:hidden flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-11 h-11 rounded-xl bg-[#1F5F5B] border border-teal-700/20 flex items-center justify-center shadow-md overflow-hidden">
              <img src="/logo.png" alt="rimmoGET" className="w-11 h-11 object-cover" />
            </div>
            <div>
              <div className="font-heading font-bold text-slate-900 text-lg leading-none">rimmoGET</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 mt-1.5">Créer un compte</div>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">Inscription</h2>
            <p className="text-sm text-slate-600">Votre demande sera validée manuellement par la direction.</p>
          </div>

          {submitted ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 space-y-3" data-testid="signup-success">
              <div className="flex items-center gap-2 text-emerald-800 font-heading font-semibold">
                <ShieldCheck className="w-5 h-5" /> Demande enregistrée
              </div>
              <p className="text-sm text-emerald-900 leading-relaxed">
                Votre demande d'inscription pour <strong>{form.society_nom}</strong> a bien été envoyée.
                La direction va examiner votre demande et activer votre compte administrateur (<strong>{form.email}</strong>).
                Vous pourrez alors vous connecter avec le mot de passe que vous avez choisi.
              </p>
              <div className="flex gap-2 pt-2">
                <Button type="button" onClick={() => navigate("/login")} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-back-login">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Retour à la connexion
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="society_nom">Nom de l'agence immobilière *</Label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="society_nom"
                      type="text"
                      required
                      minLength={2}
                      value={form.society_nom}
                      onChange={set("society_nom")}
                      className="pl-9 h-11"
                      data-testid="signup-society-input"
                      placeholder="Ex : Nom de l'agence immobilière"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_nom">Nom &amp; Prénom *</Label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="contact_nom"
                      type="text"
                      required
                      minLength={2}
                      value={form.contact_nom}
                      onChange={set("contact_nom")}
                      className="pl-9 h-11"
                      data-testid="signup-contact-nom-input"
                      placeholder="Ex : Votre nom et prénom"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email administrateur *</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="signup-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      className="pl-9 h-11"
                      data-testid="signup-email-input"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Téléphone <span className="text-slate-500 font-normal">(Numéro WhatsApp recommandé)</span></Label>
                  <div className="flex gap-2">
                    <Select value={form.dial} onValueChange={(v) => setForm((f) => ({ ...f, dial: v }))}>
                      <SelectTrigger className="h-11 w-[135px] shrink-0" data-testid="signup-dial-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={c.code} value={c.dial}>
                            <span className="mr-2">{c.flag}</span>
                            <span className="font-mono text-slate-700">{c.dial}</span>
                            <span className="ml-2 text-slate-500 text-xs">{c.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1">
                      <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        className="pl-9 h-11"
                        data-testid="signup-phone-input"
                        placeholder="Numéro"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="signup-password"
                      type="password"
                      required
                      minLength={6}
                      value={form.password}
                      onChange={set("password")}
                      className="pl-9 h-11"
                      data-testid="signup-password-input"
                      placeholder="Au moins 6 caractères"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm">Confirmation du mot de passe *</Label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="signup-confirm"
                      type="password"
                      required
                      minLength={6}
                      value={form.confirm}
                      onChange={set("confirm")}
                      className="pl-9 h-11"
                      data-testid="signup-confirm-input"
                      placeholder="Retapez votre mot de passe"
                    />
                  </div>
                </div>
                <div className="pt-1 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptTerms}
                    onCheckedChange={(v) => setAcceptTerms(v === true)}
                    className="mt-0.5"
                    data-testid="signup-accept-terms"
                  />
                  <Label htmlFor="accept-terms" className="text-sm text-slate-700 font-normal leading-relaxed cursor-pointer">
                    J'accepte les{" "}
                    <Link
                      to="/legal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2"
                      data-testid="signup-legal-link"
                    >
                      conditions d'utilisation
                    </Link>
                    {" "}et la politique de confidentialité (RGPD).
                  </Label>
                </div>
              </div>

              {error && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="signup-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !acceptTerms}
                className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-medium shadow-md shadow-orange-900/20 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="signup-submit-btn"
              >
                {loading ? "Envoi en cours…" : "Envoyer la demande"}
              </Button>

              <div className="pt-2 text-sm text-center text-slate-600">
                Déjà un compte&nbsp;?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4 decoration-orange-300 hover:decoration-orange-600 transition-colors"
                  data-testid="signup-login-link"
                >
                  Se connecter
                </Link>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
